import React, { useEffect, useState } from "react";
import axios from "../api";
import {
  Typography,
  Container,
  Paper,
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  IconButton,
  Grid,
  TextField,
  InputAdornment,
  CircularProgress
} from "@mui/material";
import {
  Visibility as PreviewIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import UndertakingDocuments from "./common/UndertakingDocuments";
import * as XLSX from "xlsx";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TableChartIcon from "@mui/icons-material/TableChart";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { toast, ToastContainer } from "react-toastify";

function AssetDetails({ onLogout }) {
  const [invites, setInvites] = useState([]); 
  const [filteredInvites, setFilteredInvites] = useState([]);
  const [records, setRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [previewDialog, setPreviewDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [, setIsActionWork] = useState(false);
  const [isActionDisabled, setIsActionDisabled] = useState(false)
  const open = Boolean(anchorEl);

  const navigate = useNavigate();
  const employee = JSON.parse(localStorage.getItem("adminUser"));
  const isEmployee = employee?.role === 'Employee';

  useEffect(() => {
    checkUserAndFetchData();
  }, []);

  useEffect(() => {
    const filtered = invites.filter((invite) => {
      const employeeInfo = getEmployeeInfo(invite);
      const searchLower = searchTerm.toLowerCase();
      return (
        invite.email?.toLowerCase().includes(searchLower) ||
        employeeInfo.employeeNo?.toLowerCase().includes(searchLower) ||
        employeeInfo.employeeName?.toLowerCase().includes(searchLower)
      );
    });
    setFilteredInvites(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, invites]);

  const checkUserAndFetchData = async () => {
    try {
      setLoading(true);
      const admin = JSON.parse(localStorage.getItem("adminUser"));   
      if (admin?.role === "Admin" || admin?.role === "NetworkOwner") {
        setIsActionWork(true);
        setIsActionDisabled(false)
        const [undertakingsResponse, invitesResponse] = await Promise.all([
          axios.get("/undertaking/all"),
          axios.get("/undertaking/invites?status=Pending")
        ]);
        console.log("invitesResponse",invitesResponse)
        setRecords(undertakingsResponse.data || []);
        setInvites(invitesResponse.data || []);
        setFilteredInvites(invitesResponse.data || []);
      } else if (admin?.role === "Employee") {
        setIsActionWork(false);
        setIsActionDisabled(true);
        const response = await axios.get("/undertaking/all");
        const allRecords = response.data || [];
        const employeeRecords = allRecords.filter(
          record => record.email?.toLowerCase() === admin.email?.toLowerCase()
        );
        const formattedData = employeeRecords.map(record => ({
          ...record,
          employeeNo: record.EmpID,
          employeeName: record.EmpName,
          status: "Accepted" 
        }));
        setRecords(employeeRecords);
        setInvites(formattedData);
        setFilteredInvites(formattedData);
      }
    } catch (err) {
      console.error("Error fetching data", err);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeInfo = (invite) => {

    const record = records.find((r) => r.email === invite.email);
    return {
      employeeNo: record?.EmpID || invite.employeeNo || "N/A",
      employeeName: record?.EmpName || invite.employeeName || "N/A",
      serialNumber: record?.serialNumber || "N/A"
    };
  };

  const hasSubmission = (email) => {
    return records.some((record) => record.email === email);
  };

  const handlePreview = (email) => {
    const record = records.find((rec) => rec.email === email);
    if (record) {
      setSelectedRecord(record);
      setPreviewDialog(true);
    } else {
      toast.warning("No submission found for this email");
    }
  };

  const handleEdit = async (email) => {
    try {
      const record = records.find((rec) => rec.email === email);
      if (record && record._id) {
        navigate(`/edit-form/${record._id}`);
      } else {
        toast.error("No submission found for this email or missing record ID");
      }
    } catch (error) {
      console.error("Error navigating to edit form:", error);
      toast.error("Error opening edit form");
    }
  };

  const handleDownload = async (email) => {
    try {
      const response = await axios.get(`/undertaking/download/${email}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `undertaking_${email}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading file", err);
      toast.error("Error downloading file");
    }
  };

  const handleDelete = async (email, id) => {
    if (window.confirm(`Are you sure you want to delete the record for ${email}?`)) {
      try {
        await axios.delete(`/undertaking/delete/${id}`);
        toast.success("Record deleted successfully");
        checkUserAndFetchData();
      } catch (err) {
        console.error("Error deleting record", err);
        toast.error("Error deleting record");
      }
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return toast.error("No File Selected");
  
    setUploading(true);
  
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
    
      const [headers, ...dataRows] = rows;
    
      const fieldMap = {
        email: "Email",
        serialnumber: "SerialNumber",
        address: "address",
        accessories: "accessories",
        assetid: "assetId",
        phonenumber: "phone",
        phone: "phone",
        empid: "EmpID",
        employeename: "EmpName",
        empname: "EmpName",
      };
    
      const formatted = dataRows.map((row) => {
        const obj = {};
        headers.forEach((key, idx) => {
          let value = row[idx];
          if (typeof value === "string") value = value.trim();
        
          const normalizedKey = key.replace(/\s+/g, "").toLowerCase();
          const mappedKey = fieldMap[normalizedKey] || 
            normalizedKey.charAt(0).toUpperCase() + normalizedKey.slice(1);
        
          if (mappedKey === "Email") value = value.toLowerCase();
          if (mappedKey === "SerialNumber") value = value.toString().trim();
          if (mappedKey === "employeeName") value = value.toString().trim();
          if (mappedKey === "empID") value = value.toString().trim();
        
          obj[mappedKey] = value;
        });
        return obj;
      });
    
      const response = await axios.post("/undertaking/excel-update-json", formatted);
    
      if (response.data.success) {
        toast.success(response.data.message || "Records updated successfully");
        if (response.data.failed && response.data.failed.length > 0) {
          const failedList = response.data.failed.map((f) => f.email || f.serialNumber || f.empID || "Unknown").join(", ");
          toast.warning(`Some records not updated: ${failedList}`);
        }
        checkUserAndFetchData();
      } else {
        toast.error(response.data.message || "Update failed");
      }
    } catch (err) {
      console.error("Excel Upload Error:", err);
      toast.error("Failed to process Excel");
    } finally {
      setUploading(false);
    }
  };

  const handleExportClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseExportMenu = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    onLogout();
    localStorage.removeItem("adminUser");
    navigate("/login");
  };

  // const handleExportExcel = () => {
  //   const pendingInvites = invites.filter((inv)=> inv?.status === 'Pending')
  //   const filteredRecords = records.map((record) => {
  //     const {
  //       assetImages,
  //       idProofPath,
  //       signaturePath,
  //       __v,
  //       _id,
  //       AccessoriesExtra,
  //       Username,
  //       location,
  //       ...rest
  //     } = record || {};
    
  //     const cleaned = {
  //       EmpName: rest.EmpName || rest.employeeName || "",
  //       ...rest,
  //       Longitude: location?.longitude || rest.longitude || "",
  //       Latitude: location?.latitude || rest.latitude || "",
  //       IpAddress: rest.ipAddress || "",
  //     };
    
  //     const capitalized = {};
  //     Object.entries(cleaned).forEach(([key, value]) => {
  //       const newKey = key.charAt(0).toUpperCase() + key.slice(1);
  //       capitalized[newKey] = value;
  //     });
    
  //     return capitalized;
  //   });
         
  //   if (!filteredRecords || filteredRecords.length === 0) {
  //     toast.error("No data to export");
  //     return;
  //   }
  
  //   const worksheet = XLSX.utils.json_to_sheet(filteredRecords);
  //   const workbook = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(workbook, worksheet, "UndertakingData");
  //   XLSX.writeFile(workbook, "Asset_Undertaking_Export.xlsx");
  //   handleCloseExportMenu();
  // };

  const handleExportExcel = () => {
  const filteredRecords = records.map((record) => {
    const {
      assetImages,
      idProofPath,
      signaturePath,
      __v,
      _id,
      AccessoriesExtra,
      Username,
      location,
      ...rest
    } = record || {};

    const cleaned = {
      EmpName: rest.EmpName || rest.employeeName || "",
      ...rest,
      Longitude: location?.longitude || rest.longitude || "",
      Latitude: location?.latitude || rest.latitude || "",
      IpAddress: rest.ipAddress || "",
    };

    const capitalized = {};
    Object.entries(cleaned).forEach(([key, value]) => {
      const newKey = key.charAt(0).toUpperCase() + key.slice(1);
      capitalized[newKey] = value;
    });

    return capitalized;
  });

  const pendingInvites = invites.filter((inv) => inv?.status === "Pending");
  const cleanedInvites = pendingInvites.map((invite) => {
    const { __v, _id, ...rest } = invite || {};
    return {
      Email: rest.email || "",
      Status: rest.status || "",
      ResendCount: rest.resendCount || 0,
      SentAt: rest.sentAt ? new Date(rest.sentAt).toLocaleString() : "",
    };
  });

  if (
    (!filteredRecords || filteredRecords.length === 0) &&
    (!cleanedInvites || cleanedInvites.length === 0)
  ) {
    toast.error("No data to export");
    return;
  }

  const workbook = XLSX.utils.book_new();

  if (filteredRecords && filteredRecords.length > 0) {
    const worksheet1 = XLSX.utils.json_to_sheet(filteredRecords);
    XLSX.utils.book_append_sheet(workbook, worksheet1, "UndertakingData");
  }

  if (cleanedInvites && cleanedInvites.length > 0) {
    const worksheet2 = XLSX.utils.json_to_sheet(cleanedInvites);
    XLSX.utils.book_append_sheet(workbook, worksheet2, "PendingInvites");
  }

  XLSX.writeFile(workbook, "Asset_Undertaking_Export.xlsx");
  handleCloseExportMenu();
  };

// In one Sheet
//   const handleExportExcel = () => {
//   const filteredRecords = records.map((record) => {
//     const {
//       assetImages,
//       idProofPath,
//       signaturePath,
//       __v,
//       _id,
//       AccessoriesExtra,
//       Username,
//       location,
//       ...rest
//     } = record || {};
  
//     const cleaned = {
//       EmpName: rest.EmpName || rest.employeeName || "",
//       ...rest,
//       Longitude: location?.longitude || rest.longitude || "",
//       Latitude: location?.latitude || rest.latitude || "",
//       IpAddress: rest.ipAddress || "",
//     };
  
//     const capitalized = {};
//     Object.entries(cleaned).forEach(([key, value]) => {
//       const newKey = key.charAt(0).toUpperCase() + key.slice(1);
//       capitalized[newKey] = value;
//     });
  
//     return capitalized;
//   });

//   const pendingInvites = invites.filter((inv) => inv?.status === "Pending");
//   const cleanedInvites = pendingInvites.map((invite) => {
//     const { __v, _id, ...rest } = invite || {};
//     return {
//       Email: rest.email || "",
//       Status: rest.status || "",
//       ResendCount: rest.resendCount || 0,
//       SentAt: rest.sentAt ? new Date(rest.sentAt).toLocaleString() : "",
//     };
//   });

//   if (
//     (!filteredRecords || filteredRecords.length === 0) &&
//     (!cleanedInvites || cleanedInvites.length === 0)
//   ) {
//     toast.error("No data to export");
//     return;
//   }

//   const mergedData = [...filteredRecords, ...cleanedInvites];

//   const worksheet = XLSX.utils.json_to_sheet(mergedData);
//   const workbook = XLSX.utils.book_new();
//   XLSX.utils.book_append_sheet(workbook, worksheet, "AllData");
//   XLSX.writeFile(workbook, "Asset_Undertaking_Export.xlsx");

//   handleCloseExportMenu();
// };

  const PreviewDialog = () => (
    <Dialog
      open={previewDialog}
      onClose={() => setPreviewDialog(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Asset Undertaking Details - {selectedRecord?.email}
      </DialogTitle>
      <DialogContent>
        {selectedRecord && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Employee Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Employee Name
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.EmpName}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Employee ID
                </Typography>
                <Typography variant="body1">{selectedRecord.EmpID}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Email
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.email}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Title
                </Typography>
                <Typography variant="body1">{selectedRecord.Title}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Phone
                </Typography>
                <Typography variant="body1">{selectedRecord.phone}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>
              Asset Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Asset ID
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.assetId}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Serial Number
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.serialNumber}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Model
                </Typography>
                <Typography variant="body1">{selectedRecord.Model}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Manufacturer
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.Manufacturer}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>
              Submission Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Submission Date
                </Typography>
                <Typography variant="body1">
                  {new Date(selectedRecord.timestamp).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Terms Accepted
                </Typography>
                <Chip
                  label={selectedRecord.termsAccepted ? "Yes" : "No"}
                  color={selectedRecord.termsAccepted ? "success" : "error"}
                  size="small"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <UndertakingDocuments
              idProofPath={selectedRecord.idProofPath}
              signaturePath={selectedRecord.signaturePath}
              assetImages={selectedRecord.assetImages}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPreviewDialog(false)} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <AdminLayout title="Asset Details" onLogout={handleLogout}>
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h5">Employee Overview</Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  size="small"
                  placeholder="Search employees..."
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 300 }}
                />
                {!isEmployee && (
                <IconButton
                  color="secondary"
                  title="Upload Excel"
                  aria-label="Upload Excel"
                  component="label"
                  disabled={uploading}
                  sx={{
                    position: "relative",
                    overflow: "hidden",
                    width: 40,
                    height: 40,
                  }}
                >

                  {uploading ? <CircularProgress size={24} /> : <CircularProgress size={24} />}

                  <UploadFileIcon />
                  <input
                    type="file"
                    accept=".xlsx, .xls"
                    onChange={(e) => handleExcelUpload(e,setUploading)}
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      width: "100%",
                      height: "100%",
                      opacity: 0,
                      cursor: "pointer"
                    }}
                  />
                </IconButton>
                )}

                <IconButton
                  color="primary"
                  onClick={handleExportClick}
                  title="Export Options"
                  aria-label="Export options"
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleCloseExportMenu}
                >
                  <MenuItem onClick={handleExportExcel}>
                    <TableChartIcon fontSize="small" sx={{ mr: 1 }} />
                    Export as Excel
                  </MenuItem>
                </Menu>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate("/dashboard")}
                  disabled={isActionDisabled}
                >
                  Back to Dashboard
                </Button>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Paper elevation={3} sx={{p:3, mb:4, overflowX:"auto"}}>
              <Box sx={{ width: "100%", overflowX:"auto" }}>
              <Table sx={{ minWidth:900, tableLayout:"fixed" }} >
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell><strong>Employee No</strong></TableCell>
                    <TableCell><strong>Employee Name</strong></TableCell>
                    <TableCell style={{width:"25%"}}><strong>Email</strong></TableCell>
                    <TableCell><strong>Serial Number</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredInvites.map((invite, index) => {
                    const hasSubmit = hasSubmission(invite.email);
                    const employeeInfo = getEmployeeInfo(invite);
                    const status = hasSubmit ? "Accepted" : invite.status;

                    return (
                      <TableRow key={invite._id || index} hover>
                        <TableCell>{employeeInfo.employeeNo}</TableCell>
                        <TableCell>{employeeInfo.employeeName}</TableCell>
                        <TableCell>{invite.email}</TableCell>
                        <TableCell>{employeeInfo.serialNumber}</TableCell>
                        <TableCell>
                          <Chip
                            label={status}
                            color={
                              status === "Accepted"
                                ? "success"
                                : status === "Pending"
                                ? "warning"
                                : "default"
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                            <IconButton
                              size="small" 
                              color="primary"
                              onClick={() => handlePreview(invite.email)}
                              title="Preview"
                              aria-label={`Preview undertaking for ${invite.email}`}
                              disabled={!hasSubmit}
                            >
                              <PreviewIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleEdit(invite.email)}
                              title="Edit"
                              aria-label={`Edit undertaking for ${invite.email}`}
                              disabled={isActionDisabled || status !== "Accepted"}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleDownload(invite.email)}
                              title="Download"
                              aria-label={`Download undertaking for ${invite.email}`}
                              disabled={ !hasSubmit}
                            >
                              <DownloadIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(invite.email, invite._id)}
                              title="Delete"
                              aria-label={`Delete invite for ${invite.email}`}
                              disabled={isActionDisabled}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </Box>
              </Paper>
            )}

            {filteredInvites.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1" color="textSecondary">
                  No invites found
                </Typography>
              </Box>
            )}
          </Paper>
        <ToastContainer position="top-center" autoClose={3000} />
        </Container>

      <PreviewDialog />
    </AdminLayout>
  );
}

export default AssetDetails;